import EditHeader from "common/components/EditHeader/EditHeader";
import HeaderButtonsEdit from "common/components/EditHeader/HeaderButtonsEdit";
import FormControl from "common/components/FormControl/FormControl";
import FormScrollError from "common/components/FormScrollError/FormScrollError";
import useRouter from "common/hooks/use-router";
import { useMemo, useState } from "react";
import { Form } from "react-final-form";
import { useTranslation } from "react-i18next";
import arrayMutators from "final-form-arrays";
import usePark from "common/hooks/usePark";
import GenusSelect from "components/select/GenusSelect/GenusSelect";
import { Genus } from "types/Genus";
import { Tag } from "types/Tag";
import TagSelect from "components/select/TagSelect/TagSelect";
import { Superfamily } from "types/Superfamily";
import ObservationGridSelect from "components/select/ObservationGridSelect/ObservationGridSelect";
import useAuth from "common/hooks/useAuth";

type Props = {
  superfamily: Superfamily;
  onSubmit: (value?: any) => any;
};

function SuperfamilyForm({ onSubmit, superfamily }: Props) {
  const { t } = useTranslation();
  const [errorMessage, setErrorMessage] = useState("");
  const { navigate } = useRouter();
  const { park: currentPark } = usePark();
  const { user: currentUser } = useAuth();
  const canManageSuperfamily = superfamily?.id
    ? (currentUser?.isAdmin ||
        currentUser?.isSuperadmin ||
        ((currentUser?.isManager || currentUser?.isObserver) &&
          currentUser?.administrationPermissions?.superfamily?.update)) ??
      false
    : (currentUser?.isAdmin ||
        currentUser?.isSuperadmin ||
        ((currentUser?.isManager || currentUser?.isObserver) &&
          currentUser?.administrationPermissions?.superfamily?.create)) ??
      false;

  const initialValues = useMemo(
    () =>
      superfamily?.id
        ? {
            ...superfamily,
            park: superfamily.park?.id ?? currentPark?.id,
            genera: superfamily.genera?.map((g: Genus) => g.id),
            tags: superfamily?.tags?.map((tag: Tag) => tag.id),
            observationGrids: superfamily?.observationGrids?.map(
              (grid) => grid.id
            ),
          }
        : {
            name: "",
            scientificName: "",
            park: currentPark?.id,
          },
    [superfamily, currentPark]
  );

  const onSubmitWrapper = async (values: any) => {
    const res = await onSubmit(values);

    if (res.message) {
      setErrorMessage(res.message);
    }
  };

  return (
    <Form
      onSubmit={onSubmitWrapper}
      subscription={{ values: true, submitting: true, pristine: true }}
      initialValues={initialValues}
      mutators={{
        ...arrayMutators,
        setFieldValue: ([field]: any, state: any, utils: any) => {
          utils.changeValue(state, field.field, () => field.value);
        },
      }}
      render={({ handleSubmit, values }) => {
        return (
          <div className="mx-auto max-w-full py-4 px-4 text-slate-500 text-base">
            {/* Header */}
            <EditHeader
              title={t(
                superfamily?.id ? "editItem.superfamily" : "newItem.superfamily"
              )}
              subtitle={values.name}
              icon="GiCheckboxTree"
              errorMessage={errorMessage}
              HeaderButtonsEdit={
                <HeaderButtonsEdit
                  inHeader
                  onSubmit={canManageSuperfamily ? handleSubmit : undefined}
                  onCancel={() => navigate("/superfamilies")}
                />
              }
            />
            <form onSubmit={handleSubmit} noValidate>
              <FormScrollError />

              <div className="responsive-flex-lg gap-2 mt-2">
                <div className="lg:flex-1 flex flex-col gap-2 white-box">
                  <div className="flex flex-row gap-4">
                    <div className="flex flex-col w-full gap-2">
                      <FormControl
                        name="name"
                        title={t("common.name")}
                        required
                        editable={canManageSuperfamily}
                      />
                    </div>
                    <div className="flex flex-col w-full gap-2">
                      <FormControl
                        name="scientificName"
                        title={t("forms.scientificName")}
                        editable={canManageSuperfamily}
                      />
                    </div>
                  </div>
                  <div className="flex flex-row gap-4 w-full">
                    <div className="flex flex-col w-full gap-2">
                      <FormControl
                        name="genera"
                        title={t("forms.genera")}
                        type="custom"
                        editable={canManageSuperfamily}
                      >
                        <GenusSelect multiple />
                      </FormControl>
                    </div>
                    <div className="flex flex-col w-full gap-2">
                      <FormControl
                        title={t("forms.observationGrids")}
                        type="custom"
                        isSelect
                        name="observationGrids"
                        editable={canManageSuperfamily}
                        multiple
                      >
                        <ObservationGridSelect />
                      </FormControl>
                    </div>
                    <div className="flex flex-col w-full gap-2">
                      <FormControl
                        name="tags"
                        title={t("forms.tags")}
                        type="custom"
                        editable={canManageSuperfamily}
                      >
                        <TagSelect multiple />
                      </FormControl>
                    </div>
                  </div>
                </div>
              </div>

              <HeaderButtonsEdit
                onSubmit={handleSubmit}
                onCancel={() => navigate("/superfamilies")}
              />
            </form>
          </div>
        );
      }}
    />
  );
}

export default SuperfamilyForm;
